'use client'

import { useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { DownloadIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import LocaleCombobox from '@/components/ui/locale-combobox'
import { useDownloadOrderExport } from '@/hooks'
import { cn } from '@/lib/utils'

type PropsType = {
  orderId: string
}

export default function OrderExportDialog({ orderId }: PropsType) {
  const t = useTranslations()
  const locale = useLocale()

  const [open, setOpen] = useState(false)
  const [exportType, setExportType] = useState<'excel' | 'pdf'>('pdf')
  const [exportLocale, setExportLocale] = useState(locale)

  const { mutate: downloadExport, isPending: isExporting } = useDownloadOrderExport()

  const handleDownload = () => {
    downloadExport({ id: orderId, type: exportType, locale: exportLocale }, { onSuccess: () => setOpen(false) })
  }

  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" variant="outline">
        <DownloadIcon className="h-4 w-4" />
        <span className="hidden lg:inline">{t('export')}</span>
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-sm" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle>{t('export')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>{t('format')}</Label>
              <RadioGroup
                value={exportType}
                onValueChange={v => setExportType(v as 'excel' | 'pdf')}
                className="grid grid-cols-2">
                {(['pdf', 'excel'] as const).map(type => (
                  <label
                    key={type}
                    className={cn(
                      'flex cursor-pointer items-center gap-2 rounded-md border px-2 py-1 shadow-xs transition-colors',
                      exportType === type ? 'border-primary bg-primary/5' : 'border-border hover:bg-accent'
                    )}>
                    <RadioGroupItem value={type} />
                    <span className="font-medium">{type === 'pdf' ? 'PDF' : 'Excel'}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="export-locale">{t('language')}</Label>
              <LocaleCombobox
                id="export-locale"
                value={exportLocale}
                onValueChange={v => setExportLocale(v || locale)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isExporting}>
              {t('cancel')}
            </Button>
            <Button disabled={isExporting} onClick={handleDownload}>
              <DownloadIcon className="h-4 w-4" />
              {t('download')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
